import { useMutation } from "@tanstack/vue-query";
import { useRouter } from "vue-router";
import { sessionClient } from "@/di/api";
import { api } from "@/di/api";
import { authRepo, userRepo } from "@/di/repositories";
import {
  createPsi as createPsiUC,
  login as loginUC,
  syncSession,
  updateUser,
} from "@/di/useCases";
import type {
  ForgotPasswordValues,
  LoginValues,
  ResetPasswordValues,
} from "@/schemas/auth";
import type {
  CreatePsiInput,
  UpdateUserInput,
} from "@phurshell/psiapp-frontend-core/domain";
import paths from "@/router/paths";

export function useCreatePsi() {
  return useMutation({
    mutationFn: (values: CreatePsiInput) => createPsiUC.execute(values),
  });
}

export function useLogin() {
  const router = useRouter();

  return useMutation({
    mutationFn: (values: LoginValues) => loginUC.execute(values),
    onSuccess: () => router.push(paths.home),
  });
}

export function useLogout() {
  const router = useRouter();

  return () => {
    sessionClient.destroySession();
    router.push(paths.login);
  };
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (values: ForgotPasswordValues) =>
      authRepo.sendRecoveryEmail(values.email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (
      values: ResetPasswordValues & { email: string; token: string },
    ) => {
      const { password_confirmation: _pc, ...rest } = values;
      return api.post("password/reset", rest);
    },
  });
}

export function useSyncSession() {
  return useMutation({
    mutationFn: () => syncSession.execute(),
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: ({
      id,
      ...params
    }: { id: number } & Partial<UpdateUserInput>) =>
      updateUser.execute(id, params),
  });
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: (id: number) => userRepo.deleteUser(id),
  });
}
